import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { db } from "../../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  onSnapshot
} from "firebase/firestore";
// Import the existing team permissions hook
import useTeamPermissions from "../../hooks/UseTeamPermissions";

function PlayerStatsLeaderPage() {
  const { teamId } = useParams();
  const [leaders, setLeaders] = useState(null);
  const [teamName, setTeamName] = useState("Team");
  const [teamLogo, setTeamLogo] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [debugInfo, setDebugInfo] = useState([]);
  
  // Use the existing team permissions hook
  const { 
    hasPermission, 
    loading: permissionsLoading, 
    role,
    isTeamPro,
    userIsPro,
    teamData
  } = useTeamPermissions(teamId);

  // Add debug logs for troubleshooting
  const addDebugLog = (message) => {
    console.log(message);
    setDebugInfo(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  // Define fetchLeaders as a useCallback to avoid recreation on each render
  const fetchLeaders = useCallback(async (forceRefresh = false) => {
    // SIMPLIFIED: Allow access if user is on the team AND either team is pro or user is pro
    const canAccessStats = hasPermission('view_player_stats');
    if (!canAccessStats) {
      addDebugLog("Permission denied for stats. Team Pro: " + isTeamPro + ", User Pro: " + userIsPro);
      setError("You don't have permission to view team statistics.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    if (forceRefresh) {
      setIsRefreshing(true);
    }

    try {
      addDebugLog("Starting stats fetch for team ID: " + teamId);
      // First, try to get cached leaders
      if (!forceRefresh) {
        const leadersRef = doc(db, "leaders", teamId);
        const leadersSnap = await getDoc(leadersRef);
        
        if (leadersSnap.exists()) {
          const data = leadersSnap.data();
          // Check if the data is less than 6 hours old
          const timestamp = data.updatedAt?.toDate();
          if (timestamp && (new Date() - timestamp) < 6 * 60 * 60 * 1000) {
            addDebugLog("Using cached leader stats from " + timestamp);
            setLeaders(data);
            setLastUpdated(timestamp);
            setLoading(false);
            setIsRefreshing(false);
            return;
          } else {
            addDebugLog("Cached data found but too old, recalculating");
          }
        } else {
          addDebugLog("No cached leader stats found, calculating from scratch");
        }
      }

      // If we get here, we need to calculate the stats from scratch
      addDebugLog("Calculating stats from scratch");
      
      // STEP 1: Fetch all game doc IDs for this team using multiple approaches
      const gameIds = [];
      
      // Try the direct teamId approach first (new data model)
      const teamGamesQuery = query(
        collection(db, "games"),
        where("teamId", "==", teamId)
      );
      
      const teamGamesSnap = await getDocs(teamGamesQuery);
      teamGamesSnap.forEach((g) => gameIds.push(g.id));
      addDebugLog(`Found ${teamGamesSnap.size} games with teamId query`);
      
      // Try home/away approach (old data model)
      const homeQuery = query(
        collection(db, "games"),
        where("homeTeamId", "==", teamId)
      );
      const awayQuery = query(
        collection(db, "games"),
        where("awayTeamId", "==", teamId)
      );

      const [homeSnap, awaySnap] = await Promise.all([
        getDocs(homeQuery),
        getDocs(awayQuery),
      ]);

      homeSnap.forEach((g) => gameIds.push(g.id));
      awaySnap.forEach((g) => gameIds.push(g.id));
      addDebugLog(`Found ${homeSnap.size} home games and ${awaySnap.size} away games`);
      
      // Try array-contains approach (third data model)
      const arrayTeamsQuery = query(
        collection(db, "games"),
        where("teams", "array-contains", teamId)
      );
      
      const arrayTeamsSnap = await getDocs(arrayTeamsQuery);
      arrayTeamsSnap.forEach((g) => gameIds.push(g.id));
      addDebugLog(`Found ${arrayTeamsSnap.size} games with teams array query`);
      
      // Remove duplicates
      const uniqueGameIds = [...new Set(gameIds)];
      addDebugLog(`Total unique games found: ${uniqueGameIds.length}`);

      // STEP 2: For each game, fetch playerStats with gameId == game doc ID
      const playerTotals = {};
      let processedStatsCount = 0;
      
      // First try to get stats with teamId filter if available
      const teamStatsQuery = query(
        collection(db, "playerStats"),
        where("teamId", "==", teamId)
      );
      
      const teamStatsSnap = await getDocs(teamStatsQuery);
      
      // Process stats with teamId filter
      teamStatsSnap.forEach((docSnap) => {
        const data = docSnap.data();
        const pId = data.playerId;
        
        if (!pId) {
          addDebugLog(`Warning: Found stat without playerId: ${docSnap.id}`);
          return;
        }
        
        if (!playerTotals[pId]) {
          playerTotals[pId] = {
            totalPoints: 0,
            totalRebounds: 0,
            totalAssists: 0,
            totalSteals: 0,
            totalBlocks: 0,
            gamesPlayed: 0,
          };
        }
        
        playerTotals[pId].totalPoints += data.points || 0;
        playerTotals[pId].totalRebounds += data.rebounds || 0;
        playerTotals[pId].totalAssists += data.assists || 0;
        playerTotals[pId].totalSteals += data.steals || 0;
        playerTotals[pId].totalBlocks += data.blocks || 0;
        playerTotals[pId].gamesPlayed += 1;
        processedStatsCount++;
      });
      
      addDebugLog(`Processed ${processedStatsCount} stats with teamId filter`);
      
      // If we didn't find any stats with teamId filter, fall back to gameId approach
      if (processedStatsCount === 0) {
        addDebugLog("No stats found with teamId filter, trying gameId approach");
        for (const gId of uniqueGameIds) {
          const statsQuery = query(
            collection(db, "playerStats"),
            where("gameId", "==", gId)
          );
          const statsSnap = await getDocs(statsQuery);
          
          statsSnap.forEach((docSnap) => {
            const data = docSnap.data();
            const pId = data.playerId;
            
            if (!pId) {
              addDebugLog(`Warning: Found stat without playerId for game ${gId}`);
              return;
            }
            
            if (!playerTotals[pId]) {
              playerTotals[pId] = {
                totalPoints: 0,
                totalRebounds: 0,
                totalAssists: 0,
                totalSteals: 0,
                totalBlocks: 0,
                gamesPlayed: 0,
              };
            }
            
            playerTotals[pId].totalPoints += data.points || 0;
            playerTotals[pId].totalRebounds += data.rebounds || 0;
            playerTotals[pId].totalAssists += data.assists || 0;
            playerTotals[pId].totalSteals += data.steals || 0;
            playerTotals[pId].totalBlocks += data.blocks || 0;
            playerTotals[pId].gamesPlayed += 1;
            processedStatsCount++;
          });
        }
        
        addDebugLog(`Processed ${processedStatsCount} stats total with gameId approach`);
      }
      
      const playerIds = Object.keys(playerTotals);
      addDebugLog(`Found stats for ${playerIds.length} unique players`);

      // STEP 3: Fetch each player's name using multiple approaches
      const playerArray = [];
      
      for (const [playerId, totals] of Object.entries(playerTotals)) {
        addDebugLog(`Processing player ${playerId}`);
        
        let playerName = "Unknown Player";
        let playerPosition = "";
        let playerImage = "";
        let playerFound = false;
        
        // Try to get player from players collection first
        try {
          const playerRef = doc(db, "players", playerId);
          const playerSnap = await getDoc(playerRef);
          
          if (playerSnap.exists()) {
            const pData = playerSnap.data();
            playerName = `${pData.firstName || ""} ${pData.lastName || ""}`.trim();
            playerPosition = pData.position || "";
            playerImage = pData.imageUrl || pData.profileImage || "";
            playerFound = true;
            addDebugLog(`Found player in players collection: ${playerName}`);
          }
        } catch (err) {
          addDebugLog(`Error fetching player from players collection: ${err.message}`);
        }
        
        // If player not found in players collection, try rosters
        if (!playerFound) {
          try {
            // Try to find the player in rosters collection
            const rosterQuery = query(
              collection(db, "rosters"),
              where("playerId", "==", playerId),
              where("teamId", "==", teamId)
            );
            
            const rosterSnap = await getDocs(rosterQuery);
            
            if (!rosterSnap.empty) {
              const rosterData = rosterSnap.docs[0].data();
              
              // Use playerName from roster if available
              if (rosterData.playerName) {
                const nameParts = rosterData.playerName.split(' ');
                const firstName = nameParts[0] || '';
                const lastName = nameParts.slice(1).join(' ') || '';
                
                playerName = rosterData.playerName;
                playerPosition = rosterData.position || "";
                playerImage = rosterData.playerImage || "";
                playerFound = true;
                addDebugLog(`Found player in rosters collection: ${playerName}`);
              }
            }
          } catch (err) {
            addDebugLog(`Error fetching player from rosters: ${err.message}`);
          }
        }
        
        // If still not found, check roster entries collection
        if (!playerFound) {
          try {
            const rosterEntriesQuery = query(
              collection(db, "rosterEntries"),
              where("playerId", "==", playerId)
            );
            
            const rosterEntriesSnap = await getDocs(rosterEntriesQuery);
            
            if (!rosterEntriesSnap.empty) {
              const entryData = rosterEntriesSnap.docs[0].data();
              
              if (entryData.playerName) {
                playerName = entryData.playerName;
                playerPosition = entryData.position || "";
                playerImage = entryData.playerImage || "";
                playerFound = true;
                addDebugLog(`Found player in rosterEntries collection: ${playerName}`);
              }
            }
          } catch (err) {
            addDebugLog(`Error fetching from rosterEntries: ${err.message}`);
          }
        }
        
        // Add player to array regardless of whether we found them
        const gp = totals.gamesPlayed || 1;
        playerArray.push({
          playerId,
          playerName,
          playerPosition,
          playerImage,
          pointsPerGame: parseFloat((totals.totalPoints / gp).toFixed(1)),
          reboundsPerGame: parseFloat((totals.totalRebounds / gp).toFixed(1)),
          assistsPerGame: parseFloat((totals.totalAssists / gp).toFixed(1)),
          stealsPerGame: parseFloat((totals.totalSteals / gp).toFixed(1)),
          blocksPerGame: parseFloat((totals.totalBlocks / gp).toFixed(1)),
          totalPoints: totals.totalPoints,
          totalRebounds: totals.totalRebounds,
          totalAssists: totals.totalAssists,
          totalSteals: totals.totalSteals,
          totalBlocks: totals.totalBlocks,
          gamesPlayed: gp
        });
      }
      
      addDebugLog(`Processed ${playerArray.length} players with their stats`);

      // STEP 4: Sort each category in descending order and get the top 5
      // Filter out players with 0 games played
      const validPlayers = playerArray.filter(player => player.gamesPlayed > 0);
      addDebugLog(`Found ${validPlayers.length} players with game stats`);
      
      // If no valid players, handle empty case
      if (validPlayers.length === 0) {
        addDebugLog("No valid players found with stats");
        
        const now = new Date();
        const emptyLeaders = {
          allPlayers: [],
          topPoints: [],
          topRebounds: [],
          topAssists: [],
          topSteals: [],
          topBlocks: [],
          updatedAt: serverTimestamp(),
        };
        
        setLeaders(emptyLeaders);
        setLastUpdated(now);
        setLoading(false);
        setIsRefreshing(false);
        return;
      }
      
      // Sort and get top players for each category
      const topPoints = [...validPlayers].sort((a, b) => b.pointsPerGame - a.pointsPerGame).slice(0, 5);
      const topRebounds = [...validPlayers].sort((a, b) => b.reboundsPerGame - a.reboundsPerGame).slice(0, 5);
      const topAssists = [...validPlayers].sort((a, b) => b.assistsPerGame - a.assistsPerGame).slice(0, 5);
      const topSteals = [...validPlayers].sort((a, b) => b.stealsPerGame - a.stealsPerGame).slice(0, 5);
      const topBlocks = [...validPlayers].sort((a, b) => b.blocksPerGame - a.blocksPerGame).slice(0, 5);
      
      addDebugLog(`Top points leader: ${topPoints.length > 0 ? topPoints[0].playerName : 'None'} - ${topPoints.length > 0 ? topPoints[0].pointsPerGame : 0} PPG`);
      
      const now = new Date();
      const computedLeaders = {
        allPlayers: validPlayers,
        topPoints,
        topRebounds,
        topAssists,
        topSteals,
        topBlocks,
        updatedAt: serverTimestamp(),
      };

      setLeaders(computedLeaders);
      setLastUpdated(now);

      // STEP 5: Store the leaders in Firestore (in a "leaders" collection)
      // Only admins or coaches should be able to write to the leaders collection
      if (hasPermission('edit_player_stats')) {
        try {
          await setDoc(doc(db, "leaders", teamId), computedLeaders);
          addDebugLog("Successfully saved leaders to cache");
        } catch (saveErr) {
          addDebugLog(`Error saving leaders cache: ${saveErr.message}`);
          // Continue even if cache save fails
        }
      }
    } catch (err) {
      console.error("Error fetching leader stats:", err);
      addDebugLog(`Error fetching leader stats: ${err.message}`);
      setError(err.message);
    }
    setLoading(false);
    setIsRefreshing(false);
  }, [teamId, hasPermission, isTeamPro, userIsPro]);

  const fetchTeamDetails = useCallback(async () => {
    if (teamData) {
      // If we already have team data from the permissions hook, use it
      setTeamName(teamData.name || "Team");
      setTeamLogo(teamData.logoUrl || "");
      addDebugLog("Using team data from permissions hook");
      return;
    }
    
    try {
      const teamRef = doc(db, "teams", teamId);
      const teamSnap = await getDoc(teamRef);
      if (teamSnap.exists()) {
        const data = teamSnap.data();
        setTeamName(data.name || "Team");
        setTeamLogo(data.logoUrl || "");
        addDebugLog(`Fetched team details: ${data.name}`);
      } else {
        addDebugLog("Team document not found");
      }
    } catch (err) {
      console.error("Error fetching team details:", err);
      addDebugLog(`Error fetching team details: ${err.message}`);
    }
  }, [teamId, teamData]);

  // Setup real-time listeners for changes to games and playerStats
  useEffect(() => {
    // Only set up listeners if we have permission to view stats
    if (!hasPermission('view_player_stats')) {
      addDebugLog("Skipping listeners setup - no permissions");
      return () => {};
    }
    
    addDebugLog("Setting up real-time listeners");
    
    // Using try-catch for each listener setup to prevent failures
    let cleanupFunctions = [];
    
    try {
      // Home games listener
      const gamesListener = onSnapshot(
        query(collection(db, "games"), 
          where("homeTeamId", "==", teamId)),
        (snapshot) => {
          if (!snapshot.empty) {
            addDebugLog(`Home games listener triggered with ${snapshot.docChanges().length} changes`);
            fetchLeaders(true);
          }
        },
        (error) => addDebugLog(`Home games listener error: ${error.message}`)
      );
      cleanupFunctions.push(gamesListener);
    } catch (err) {
      addDebugLog(`Error setting up home games listener: ${err.message}`);
    }
    
    try {
      // Away games listener
      const gamesAwayListener = onSnapshot(
        query(collection(db, "games"), 
          where("awayTeamId", "==", teamId)),
        (snapshot) => {
          if (!snapshot.empty) {
            addDebugLog(`Away games listener triggered with ${snapshot.docChanges().length} changes`);
            fetchLeaders(true);
          }
        },
        (error) => addDebugLog(`Away games listener error: ${error.message}`)
      );
      cleanupFunctions.push(gamesAwayListener);
    } catch (err) {
      addDebugLog(`Error setting up away games listener: ${err.message}`);
    }
    
    try {
      // Direct team games listener
      const teamGamesListener = onSnapshot(
        query(collection(db, "games"), 
          where("teamId", "==", teamId)),
        (snapshot) => {
          if (!snapshot.empty) {
            addDebugLog(`Team games listener triggered with ${snapshot.docChanges().length} changes`);
            fetchLeaders(true);
          }
        },
        (error) => addDebugLog(`Team games listener error: ${error.message}`)
      );
      cleanupFunctions.push(teamGamesListener);
    } catch (err) {
      addDebugLog(`Error setting up team games listener: ${err.message}`);
    }

    try {
      // Player stats listener
      const statsListener = onSnapshot(
        query(collection(db, "playerStats"),
          where("teamId", "==", teamId)),
        (snapshot) => {
          if (!snapshot.empty) {
            addDebugLog(`Player stats listener triggered with ${snapshot.docChanges().length} changes`);
            fetchLeaders(true);
          }
        },
        (error) => addDebugLog(`Player stats listener error: ${error.message}`)
      );
      cleanupFunctions.push(statsListener);
    } catch (err) {
      addDebugLog(`Error setting up player stats listener: ${err.message}`);
    }

    return () => {
      addDebugLog("Cleaning up listeners");
      cleanupFunctions.forEach(cleanup => cleanup());
    };
  }, [teamId, fetchLeaders, hasPermission]);

  useEffect(() => {
    fetchTeamDetails();
    // Wait for permissions to load before fetching leaders
    if (!permissionsLoading) {
      addDebugLog("Permissions loaded, fetching leaders");
      fetchLeaders();
    }
  }, [fetchTeamDetails, fetchLeaders, permissionsLoading]);

  // Handler for refreshing stats
  const handleRefresh = () => {
    addDebugLog("Manual refresh requested");
    fetchLeaders(true);
  };

  const formatDate = (date) => {
    if (!date) return "";
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Show loading while checking permissions
  if (permissionsLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div className="loader" style={{
          border: '16px solid #f3f3f3',
          borderRadius: '50%',
          borderTop: '16px solid #3498db',
          width: '120px',
          height: '120px',
          animation: 'spin 2s linear infinite'
        }}></div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
        <p style={{ fontSize: '18px', color: '#2c3e50' }}>Checking permissions...</p>
      </div>
    );
  }

  // Check if user has permission to view stats, show error if not
  if (!hasPermission('view_player_stats')) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '40px',
        maxWidth: '600px',
        margin: '40px auto',
        backgroundColor: '#fff9f9',
        borderRadius: '8px',
        boxShadow: '0 0 10px rgba(0,0,0,0.1)',
        border: '1px solid #ffcccc'
      }}>
        <h2 style={{ color: '#e74c3c' }}>Access Restricted</h2>
        <p style={{ fontSize: '16px', marginBottom: '20px' }}>
          You don't have permission to view team statistics.
          {(!isTeamPro && !userIsPro) && " This feature requires a Pro subscription."}
        </p>
        {role !== 'admin' && (
          <p style={{ fontSize: '15px', color: '#7f8c8d', marginBottom: '20px' }}>
            Pro tip: Contact your team admin for access to player statistics or upgrade to Pro.
          </p>
        )}
      </div>
    );
  }

  if (loading && !isRefreshing) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div className="loader" style={{
          border: '16px solid #f3f3f3',
          borderRadius: '50%',
          borderTop: '16px solid #3498db',
          width: '120px',
          height: '120px',
          animation: 'spin 2s linear infinite'
        }}></div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
        <p style={{ fontSize: '18px', color: '#2c3e50' }}>Loading stats for {teamName}...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '40px',
        maxWidth: '600px',
        margin: '0 auto',
        backgroundColor: '#fff9f9',
        borderRadius: '8px',
        boxShadow: '0 0 10px rgba(0,0,0,0.1)',
        border: '1px solid #ffcccc'
      }}>
        <h2 style={{ color: '#e74c3c' }}>Error Loading Stats</h2>
        <p style={{ fontSize: '16px' }}>{error}</p>
        <button 
          style={{
            backgroundColor: '#3498db',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '4px',
            cursor: 'pointer',
            marginTop: '20px',
            fontSize: '16px'
          }}
          onClick={handleRefresh}
        >
          Try Again
        </button>
      </div>
    );
  }
  
  if (!leaders) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <h2 style={{ color: '#7f8c8d' }}>No Stats Available</h2>
        <p>There are no statistics available for this team.</p>
        <button 
          style={{
            backgroundColor: '#3498db',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '4px',
            cursor: 'pointer',
            marginTop: '20px',
            fontSize: '16px'
          }}
          onClick={handleRefresh}
        >
          Refresh Stats
        </button>
      </div>
    );
  }

  // Category mapping for the stats tab - removed minutes and efficiency
  const categoryMapping = {
    all: { title: "All Categories", data: null },
    points: { title: "Points Per Game", data: leaders.topPoints, key: "pointsPerGame" },
    rebounds: { title: "Rebounds Per Game", data: leaders.topRebounds, key: "reboundsPerGame" },
    assists: { title: "Assists Per Game", data: leaders.topAssists, key: "assistsPerGame" },
    steals: { title: "Steals Per Game", data: leaders.topSteals, key: "stealsPerGame" },
    blocks: { title: "Blocks Per Game", data: leaders.topBlocks, key: "blocksPerGame" }
  };

  return (
    <div style={{
      fontFamily: "'Roboto', sans-serif",
      backgroundColor: "#f0f4f8",
      padding: "30px 20px",
      minHeight: "100vh",
    }}>
      <div style={{
        maxWidth: "1200px",
        margin: "0 auto",
        backgroundColor: "#fff",
        borderRadius: "12px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
        overflow: "hidden"
      }}>
        {/* Header */}
        <div style={{
          padding: "30px 40px",
          borderBottom: "1px solid #eaeaea",
          background: "linear-gradient(to right, #2c3e50, #3498db)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
            {teamLogo && (
              <div style={{
                width: "70px",
                height: "70px",
                borderRadius: "12px",
                backgroundColor: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "5px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)"
              }}>
                <img 
                  src={teamLogo} 
                  alt={`${teamName} logo`} 
                  style={{ 
                    width: "60px", 
                    height: "60px", 
                    objectFit: "contain" 
                  }} 
                />
              </div>
            )}
            <div>
              <h1 style={{
                fontSize: "32px",
                color: "#ffffff",
                margin: "0",
                fontWeight: "700",
                textShadow: "0 1px 3px rgba(0,0,0,0.2)"
              }}>
                {teamName} Team Leaders
              </h1>
              {lastUpdated && (
                <p style={{
                  margin: "5px 0 0 0",
                  color: "rgba(255,255,255,0.8)",
                  fontSize: "14px"
                }}>
                  Last updated: {formatDate(lastUpdated)}
                </p>
              )}
              {(isTeamPro || userIsPro) && (
                <div style={{
                  display: "inline-block",
                  backgroundColor: "rgba(46, 204, 113, 0.2)",
                  color: "#ffffff",
                  padding: "2px 8px",
                  borderRadius: "4px",
                  fontSize: "12px",
                  marginTop: "5px",
                  border: "1px solid rgba(46, 204, 113, 0.4)"
                }}>
                  Pro Feature
                </div>
              )}
            </div>
          </div>
          <button 
            onClick={handleRefresh}
            disabled={isRefreshing}
            style={{
              backgroundColor: isRefreshing ? "#bdc3c7" : "#2ecc71",
              color: "white",
              border: "none",
              borderRadius: "8px",
              padding: "12px 24px",
              fontSize: "15px",
              cursor: isRefreshing ? "default" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              transition: "all 0.2s",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              fontWeight: "600"
            }}
          >
            <span style={{ fontWeight: "bold" }}>{isRefreshing ? "⟳" : "⟳"}</span> 
            {isRefreshing ? "Refreshing..." : "Refresh Stats"}
          </button>
        </div>

        {isRefreshing && (
          <div style={{ 
            padding: "10px 0", 
            backgroundColor: "#e3f2fd", 
            textAlign: "center",
            color: "#2980b9",
            fontWeight: "500",
            fontSize: "14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px"
          }}>
            <div className="mini-loader" style={{
              border: '3px solid #e3f2fd',
              borderRadius: '50%',
              borderTop: '3px solid #3498db',
              width: '16px',
              height: '16px',
              animation: 'spin 1s linear infinite'
            }}></div>
            Updating team statistics...
          </div>
        )}

        {/* Debug panel - Hidden by default, can be enabled for troubleshooting */}
        {false && (
          <div style={{
            padding: "12px",
            backgroundColor: "#f8f9fa",
            maxHeight: "200px",
            overflowY: "auto",
            fontSize: "12px",
            color: "#6c757d",
            borderBottom: "1px solid #eaeaea"
          }}>
            <details>
              <summary style={{ fontWeight: "bold", cursor: "pointer" }}>Debug Information</summary>
              <ul style={{ margin: "8px 0 0 0", padding: "0 0 0 16px" }}>
                {debugInfo.map((log, idx) => (
                  <li key={idx} style={{ marginBottom: "4px" }}>{log}</li>
                ))}
              </ul>
            </details>
          </div>
        )}

        {/* Stats Category Tabs */}
        <div style={{
          display: "flex",
          overflowX: "auto",
          borderBottom: "1px solid #eaeaea",
          backgroundColor: "#f8f9fa",
          padding: "0 20px"
        }}>
          {Object.keys(categoryMapping).map(category => (
            <button
              key={category}
              onClick={() => setActiveTab(category)}
              style={{
                padding: "16px 24px",
                backgroundColor: activeTab === category ? "#fff" : "transparent",
                border: "none",
                borderBottom: activeTab === category ? "3px solid #3498db" : "3px solid transparent",
                fontSize: "16px",
                color: activeTab === category ? "#3498db" : "#7f8c8d",
                cursor: "pointer",
                fontWeight: activeTab === category ? "600" : "400",
                transition: "all 0.2s",
                whiteSpace: "nowrap"
              }}
            >
              {categoryMapping[category].title}
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div style={{ padding: "30px" }}>
          {activeTab === "all" ? (
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: "30px"
            }}>
              {Object.keys(categoryMapping).filter(cat => cat !== "all").map(category => {
                const categoryData = categoryMapping[category].data || [];
                
                return (
                  <div key={category} style={{
                    backgroundColor: "#ffffff",
                    borderRadius: "12px",
                    boxShadow: "0 2px 15px rgba(0, 0, 0, 0.06)",
                    overflow: "hidden",
                    border: "1px solid #eaeaea",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    height: "100%"
                  }}>
                    <div style={{
                      background: "linear-gradient(to right, #3498db, #2980b9)",
                      color: "white",
                      padding: "18px 20px",
                      fontSize: "18px",
                      fontWeight: "600",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}>
                      <span>{categoryMapping[category].title}</span>
                      {/* Add an icon based on category */}
                      <span style={{ fontSize: "20px" }}>
                        {category === "points" ? "🏆" : 
                         category === "rebounds" ? "🔄" : 
                         category === "assists" ? "👐" : 
                         category === "steals" ? "🤲" : 
                         category === "blocks" ? "🛑" : "📊"}
                      </span>
                    </div>
                    <div style={{ padding: "8px 0" }}>
                      {categoryData.length === 0 ? (
                        <div style={{
                          padding: "24px 16px",
                          textAlign: "center",
                          color: "#7f8c8d",
                          fontSize: "15px"
                        }}>
                          No stats recorded yet
                        </div>
                      ) : (
                        categoryData.map((player, index) => (
                          <div key={player.playerId} style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "12px 20px",
                            backgroundColor: index % 2 === 0 ? "#ffffff" : "#f9fafb"
                          }}>
                            <div style={{ 
                              width: "28px", 
                              height: "28px", 
                              borderRadius: "50%", 
                              backgroundColor: index === 0 ? "#f1c40f" : index === 1 ? "#bdc3c7" : index === 2 ? "#cd7f32" : "#e0e0e0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: "bold",
                              fontSize: "14px",
                              color: index === 0 ? "#7d6608" : index === 1 ? "#7f8c8d" : index === 2 ? "#6e4b18" : "#7f8c8d",
                              marginRight: "12px"
                            }}>
                              {index + 1}
                            </div>
                            <div style={{ 
                              width: "36px", 
                              height: "36px", 
                              borderRadius: "50%",
                              backgroundColor: "#e0e0e0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              overflow: "hidden",
                              marginRight: "12px"
                            }}>
                              {player.playerImage ? (
                                <img 
                                  src={player.playerImage} 
                                  alt="" 
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }} 
                                />
                              ) : (
                                <div style={{ 
                                  fontWeight: "bold",
                                  color: "#7f8c8d",
                                  fontSize: "16px"
                                }}>
                                  {player.playerName.substring(0, 1)}
                                </div>
                              )}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: "600", fontSize: "15px" }}>{player.playerName}</div>
                              {player.playerPosition && (
                                <div style={{ fontSize: "13px", color: "#7f8c8d" }}>{player.playerPosition}</div>
                              )}
                            </div>
                            <div style={{ 
                              fontSize: "18px", 
                              fontWeight: "bold", 
                              color: index === 0 ? "#3498db" : "#2c3e50",
                              backgroundColor: index === 0 ? "rgba(52, 152, 219, 0.1)" : "transparent",
                              padding: "4px 10px",
                              borderRadius: "6px"
                            }}>
                              {player[categoryMapping[category].key]}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div>
              <h2 style={{
                fontSize: "24px",
                color: "#2c3e50",
                marginBottom: "20px",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                gap: "10px"
              }}>
                <span style={{ fontSize: "24px" }}>
                  {activeTab === "points" ? "🏆" : 
                   activeTab === "rebounds" ? "🔄" : 
                   activeTab === "assists" ? "👐" : 
                   activeTab === "steals" ? "🤲" : 
                   activeTab === "blocks" ? "🛑" : "📊"}
                </span>
                {categoryMapping[activeTab].title}
              </h2>
              
              <div style={{
                backgroundColor: "#ffffff",
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.06)",
                overflow: "hidden",
                border: "1px solid #eaeaea"
              }}>
                {(categoryMapping[activeTab].data || []).length === 0 ? (
                  <div style={{
                    padding: "40px 20px",
                    textAlign: "center",
                    color: "#7f8c8d",
                    fontSize: "16px"
                  }}>
                    No stats recorded yet for this category.
                  </div>
                ) : (
                  <table style={{
                    width: "100%",
                    borderCollapse: "collapse"
                  }}>
                    <thead>
                      <tr style={{
                        backgroundColor: "#2c3e50",
                        color: "white"
                      }}>
                        <th style={{ padding: "16px", textAlign: "center", width: "60px" }}>Rank</th>
                        <th style={{ padding: "16px", textAlign: "left" }}>Player</th>
                        <th style={{ padding: "16px", textAlign: "center" }}>Position</th>
                        <th style={{ padding: "16px", textAlign: "center" }}>Games</th>
                        <th style={{ padding: "16px", textAlign: "right" }}>{categoryMapping[activeTab].title}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categoryMapping[activeTab].data?.map((player, index) => (
                        <tr key={player.playerId} style={{
                          borderBottom: "1px solid #eaeaea",
                          backgroundColor: index % 2 === 0 ? "#ffffff" : "#f9fafb"
                        }}>
                          <td style={{ 
                            padding: "16px", 
                            textAlign: "center"
                          }}>
                            <div style={{
                              width: "36px", 
                              height: "36px", 
                              borderRadius: "50%", 
                              backgroundColor: index === 0 ? "#f1c40f" : index === 1 ? "#bdc3c7" : index === 2 ? "#cd7f32" : "#e0e0e0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: "bold",
                              fontSize: "16px",
                              color: index === 0 ? "#7d6608" : index === 1 ? "#7f8c8d" : index === 2 ? "#6e4b18" : "#7f8c8d",
                              margin: "0 auto"
                            }}>
                              {index + 1}
                            </div>
                          </td>
                          <td style={{ padding: "16px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                              {player.playerImage ? (
                                <img 
                                  src={player.playerImage} 
                                  alt={player.playerName} 
                                  style={{ 
                                    width: "48px", 
                                    height: "48px", 
                                    borderRadius: "8px",
                                    objectFit: "cover",
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)" 
                                  }} 
                                />
                              ) : (
                                <div style={{ 
                                  width: "48px", 
                                  height: "48px", 
                                  borderRadius: "8px",
                                  backgroundColor: "#e0e0e0",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontWeight: "bold",
                                  fontSize: "20px",
                                  color: "#7f8c8d"
                                }}>
                                  {player.playerName.substring(0, 1)}
                                </div>
                              )}
                              <span style={{ fontWeight: "600", fontSize: "16px" }}>{player.playerName}</span>
                            </div>
                          </td>
                          <td style={{ padding: "16px", textAlign: "center", color: "#7f8c8d", fontWeight: "500" }}>
                            {player.playerPosition || "-"}
                          </td>
                          <td style={{ padding: "16px", textAlign: "center", fontWeight: "500" }}>{player.gamesPlayed}</td>
                          <td style={{ 
                            padding: "16px", 
                            textAlign: "right", 
                            fontWeight: "bold",
                            fontSize: "20px",
                            color: index === 0 ? "#3498db" : "#2c3e50"
                          }}>
                            <span style={{
                              backgroundColor: index === 0 ? "rgba(52, 152, 219, 0.1)" : "transparent",
                              padding: "6px 12px",
                              borderRadius: "8px",
                              display: "inline-block",
                              minWidth: "60px",
                              textAlign: "center"
                            }}>
                              {player[categoryMapping[activeTab].key]}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PlayerStatsLeaderPage;